import { useState, type ReactElement } from 'react';
import {
  BILD_VORLAGEN,
  type Dreiklang,
  type Effect,
  type Op,
  TRANSITIONS,
  type TransitionKind,
  KURVEN,
  KURVEN_NAMEN,
  kurveBei,
  abweichungenJeGruppe,
  abbruchSatz,
  ausSchiebern,
  clipPatch,
  filterVorlageAus,
  transformAt,
  vorlageVon,
  weissabgleich,
  zuSchiebern,
  zuruecksetzen,
} from '@schnitt/shared';
import { useEditor } from '../store';
import { KEIN_BILD, PUNKTE_JE_SEKUNDE, verfolgeImVideo } from '../verfolgen';
import { useZeitText } from '../zeit';
import { MASKENFORMEN, MASKEN_NAMEN, maskeMittig, type Clip } from '@schnitt/shared';

/** Wie dick die Kontur einer neuen Gedankenblase ist (7.224), als Anteil der Ebene. */
const KONTUR_VORGABE = 0.012;
/** Die Wahl für die Kontur -- schlicht, wie ein Comic: ohne, schwarz, weiss. */
const KONTUR_FARBEN: [string, string | null][] = [
  ['Ohne Kontur', null],
  ['Schwarz', '#000000'],
  ['Weiß', '#ffffff'],
];
import { Gruppe, Sheet, Slider } from './Sheet';
import { VorlagenSheet } from './VorlagenSheet';
import { EffektKachel, useKachelbild } from './EffektKachel';

const TRANSITION_LABELS: Record<TransitionKind, string> = {
  crossfade: 'Überblendung',
  dipToBlack: 'Über Schwarz',
  dipToWhite: 'Über Weiß',
  wipeleft: 'Wisch ←',
  wiperight: 'Wisch →',
  wipeup: 'Wisch ↑',
  wipedown: 'Wisch ↓',
  slideleft: 'Schub ←',
  slideright: 'Schub →',
  circleopen: 'Kreis auf',
  circleclose: 'Kreis zu',
  horzopen: 'Balken —',
  vertopen: 'Balken |',
  diagbr: 'Diag ↘',
  diagtl: 'Diag ↖',
  radial: 'Radial',
  pixelize: 'Pixel',
};

/** Ein Schieber der Farbräder in Worten: neutral, oder wie weit in welche Richtung. */
const radText = (v: number): string =>
  Math.abs(v) < 0.005 ? 'neutral' : `${v > 0 ? '+' : '−'}${Math.round(Math.abs(v) * 100)}`;

const LOOKS = [
  { name: 'teal-orange', label: 'Kino' },
  { name: 'warm', label: 'Warm' },
  { name: 'cool', label: 'Kühl' },
  { name: 'vivid', label: 'Kräftig' },
  { name: 'bw', label: 'Schwarzweiß' },
  { name: 'flat-log', label: 'Flach' },
];

/**
 * Everything about the selected clip: timing, sound, look, transition.
 *
 * **Zwei Komponenten, und das ist kein Schönheitsfehler (7.81).** Hier vorn
 * stehen genau zwei Hooks, dann fällt die Entscheidung, dann kommt das Blatt
 * als eigene Komponente. Vorher stand alles in einer: vier Hooks, ein
 * vorzeitiges `return`, wenn nichts gewählt ist -- und danach acht weitere
 * Hooks.
 *
 * Damit rendert dieselbe Komponente einmal zwölf Hooks und einmal vier. React
 * bricht dann ab (`Rendered fewer hooks than expected`, Fehler 300), und wer
 * es trifft, sieht eine weisse Fläche. Ausgelöst wird es von etwas ganz
 * Alltäglichem: Blatt offen, Clip abgewählt -- am schnellen Finger häufiger
 * als am langsamen, deshalb fiel es erst im Durchgang bei einem Viertel der
 * Wartezeiten auf.
 *
 * Eine eigene Komponente kann das nicht mehr: sie gibt es nur, wenn es einen
 * Clip gibt.
 */
export function InspectorSheet({
  onClose,
  onOpenSpur,
  onOpenTon,
}: {
  onClose: () => void;
  onOpenSpur: (trackId: string) => void;
  /** Der Pegel über die Zeit, mit Punkten (das Blatt „Ton“). */
  onOpenTon: () => void;
}): ReactElement {
  const selection = useEditor((s) => s.selection);
  const clipById = useEditor((s) => s.clipById);
  const found = selection.length === 1 ? clipById(selection[0]!) : null;

  if (!found) {
    return (
      <Sheet title="Clip" onClose={onClose}>
        <p className="hint">
          {selection.length > 1
            ? 'Mehrere Clips ausgewählt. Tippe einen einzelnen an, um ihn zu bearbeiten.'
            : 'Tippe in der Timeline auf einen Clip.'}
        </p>
      </Sheet>
    );
  }
  return <ClipBlatt clip={found.clip} trackId={found.trackId} onClose={onClose} onOpenSpur={onOpenSpur} onOpenTon={onOpenTon} />;
}

function ClipBlatt({
  clip,
  trackId,
  onClose,
  onOpenSpur,
  onOpenTon,
}: {
  clip: Clip;
  trackId: string;
  onClose: () => void;
  onOpenSpur: (trackId: string) => void;
  onOpenTon: () => void;
}): ReactElement {
  const zeit = useZeitText();
  const dispatch = useEditor((s) => s.dispatch);
  const timeline = useEditor((s) => s.timeline);
  const isBaseTrack = timeline.tracks.find((t) => t.kind === 'video')?.id === trackId;
  const track = timeline.tracks.find((t) => t.id === trackId) ?? null;
  const playhead = useEditor((s) => s.playhead);
  const setPlayhead = useEditor((s) => s.setPlayhead);
  const fps = timeline.settings.fps;
  const local = Math.min(clip.duration, Math.max(0, playhead - clip.start));
  /** Was an diesem Clip vom Standard abweicht -- je Gruppe (7.15). Eine
   *  Rechnung für drei Zwecke: die Zahl an der Überschrift, das Aufklappen
   *  und das Zurücksetzen. */
  const geaendert = abweichungenJeGruppe(clip);
  /**
   * Der Frame, auf dem die Kacheln zeigen (7.16), und die Effekte des Clips
   * *ohne* das, was die jeweilige Reihe gerade auswählt. So zeigt jede Kachel
   * den Clip, wie er mit ihr aussähe -- mit allem anderen, was schon dransteht.
   */
  const assetId = clip.source.kind === 'asset' ? clip.source.assetId : null;
  const posterUrl = useEditor((s) => (assetId ? s.assets.find((a) => a.id === assetId)?.posterUrl : undefined));
  const kachelbild = useKachelbild(posterUrl ?? undefined);
  const ohneLook = clip.effects.filter((e) => e.type !== 'lut');
  const ohneFarbe = clip.effects.filter((e) => e.type !== 'colorAdjust');
  const vorlage = vorlageVon(clip);
  const keyframes = [...clip.keyframes].sort((a, b) => a.at - b.at);
  const keyframeWahl = useEditor((s) => s.keyframeWahl);
  const setKeyframes = (next: typeof keyframes) => dispatch({ op: 'updateClip', clipId: clip.id, patch: { keyframes: next } } as Op);
  /** Set (or overwrite) the keyframe under the playhead with what is on screen now. */
  const setKeyframeHere = () => {
    const transform = transformAt(clip, local);
    const rest = keyframes.filter((k) => Math.abs(k.at - local) >= 1 / fps);
    // Der neue erbt die Kurve, die an dieser Stelle gerade gilt (S30) -- wer
    // eine gleichförmige Fahrt in der Mitte teilt, will keine halbe weiche.
    setKeyframes([...rest, { at: local, transform, kurve: kurveBei(clip, local) }]);
  };
  const patch = (values: Record<string, unknown>) =>
    dispatch({ op: 'updateClip', clipId: clip.id, patch: values } as Op);
  const [vorlagen, setVorlagen] = useState(false);
  /**
   * Der Keyframe, dessen Verlauf gerade eingestellt wird (S30): der gewählte,
   * sonst der letzte vor dem Abspielkopf. Der letzte hat keine Strecke mehr
   * vor sich -- er bleibt trotzdem wählbar, damit die Reihe nicht springt.
   */
  const gewaehlterKeyframe =
    keyframes.find((k) => keyframeWahl?.clipId === clip.id && Math.abs(k.at - keyframeWahl.at) < 1e-6) ??
    [...keyframes].reverse().find((k) => k.at <= local + 1e-6) ??
    keyframes[0]!;

  /**
   * Die Ebene an etwas im Bild hängen (S13).
   *
   * Sie steht schon dort, wo das Motiv ist -- der Mensch hat sie hingeschoben.
   * Von da an übernimmt das Verfolgen und schreibt Keyframes, also genau das,
   * womit sich eine Ebene ohnehin bewegt: wer das Ergebnis nicht mag, zieht
   * einzelne Punkte von Hand.
   *
   * Was vor dieser Stelle steht, bleibt stehen; was danach kam, ersetzt die
   * Spur. Alles andere wäre zwei Bewegungen übereinander.
   */
  const toast = useEditor((s) => s.toast);
  const assets = useEditor((s) => s.assets);
  const tonAsset = clip.source.kind === 'asset' ? assets.find((a) => a.id === (clip.source as { assetId: string }).assetId) : undefined;
  /** Ein Clip mit Ton: ein Video oder eine Tonaufnahme, kein Bild. */
  const mitTon = Boolean(tonAsset && tonAsset.kind !== 'image');
  const pegelVerlauf = (clip.gainKeyframes ?? []).length > 0;
  const [verfolgt, setVerfolgt] = useState<number | null>(null);
  /**
   * Was unter dieser Ebene liegt und ein Bild hat.
   *
   * Nicht „die Basisspur“: welche Spur unten liegt, entscheidet der Schnitt,
   * und in Saschas Projekten liegt das Material oft auf einer mittleren, weil
   * darunter noch eine leere Kameraspur steht. Gesucht wird deshalb von der
   * eigenen Spur aus nach unten, und genommen wird die erste, die an dieser
   * Stelle wirklich etwas zeigt.
   */
  const videoSpuren = timeline.tracks.filter((t) => t.kind === 'video');
  const eigeneSpur = videoSpuren.findIndex((t) => t.id === trackId);
  const basis = (() => {
    for (const spur of videoSpuren.slice(0, Math.max(0, eigeneSpur)).reverse()) {
      if (spur.hidden) continue;
      const treffer = spur.clips.find(
        (c) => c.source.kind === 'asset' && playhead >= c.start && playhead < c.start + c.duration,
      );
      if (treffer) {
        return {
          clip: treffer,
          mediaTime: treffer.sourceIn + (playhead - treffer.start) * treffer.speed,
        };
      }
    }
    return null;
  })();
  const basisQuelle = basis?.clip.source;
  const basisSrc =
    basisQuelle?.kind === 'asset'
      ? (assets.find((a) => a.id === basisQuelle.assetId)?.proxyUrl ?? null)
      : null;

  const verfolgen = async () => {
    if (!basis || !basisSrc) return;
    const jetzt = transformAt(clip, local);
    const tempo = Math.min(4, Math.max(0.25, basis.clip.speed));
    // Nur so weit, wie *beide* reichen: die Ebene und das Grundmaterial
    // darunter. Über den Schnitt hinaus zu verfolgen hieße, im nächsten Clip
    // nach einem Gesicht zu suchen, das dort nie war.
    const dauer = Math.max(
      0,
      Math.min(clip.duration - local, basis.clip.start + basis.clip.duration - playhead),
    );
    setVerfolgt(0);
    try {
      const ergebnis = await verfolgeImVideo({
        src: basisSrc,
        von: basis.mediaTime,
        bis: basis.mediaTime + dauer * tempo,
        clipProMedien: 1 / tempo,
        ersterPunkt: local,
        x: jetzt.x,
        y: jetzt.y,
        fortschritt: setVerfolgt,
      });
      const davor = keyframes.filter((k) => k.at < local - 1e-6);
      // Verfolgte Punkte stehen dicht an dicht; dazwischen ist gleichförmig
      // richtig -- eine Kurve je Zehntelsekunde wäre ein Ruckeln mit Formel.
      setKeyframes([
        ...davor,
        ...ergebnis.punkte.map((p) => ({ at: p.at, transform: { ...jetzt, x: p.x, y: p.y }, kurve: 'linear' as const })),
      ]);
      if (ergebnis.grund === 'kein-bild') toast('error', KEIN_BILD);
      else if (ergebnis.grund) toast('info', `${abbruchSatz(ergebnis.grund)} (bis ${zeit(ergebnis.bis, true)})`);
      else toast('success', `Verfolgt bis ${zeit(ergebnis.bis, true)}, ${ergebnis.punkte.length} Punkte.`);
    } catch (fehler) {
      toast('error', fehler instanceof Error ? fehler.message : 'Das Verfolgen ging nicht.');
    } finally {
      setVerfolgt(null);
    }
  };

  /**
   * Ein Clip auf einer Tonspur hat kein Bild -- also auch keinen Ausschnitt,
   * keine Farbe und keine Deckkraft. Sascha am 4. September: „Voiceover /
   * Audios haben ja gerade dieselben Optionen wie ein Bild/Video“ (7.22).
   *
   * Die Spur entscheidet, nicht die Datei: eine Tonspur zeigt nichts, auch
   * wenn jemand ein Video darauflegt, um nur den Ton zu behalten.
   */
  const nurTon = track?.kind === 'audio';

  /** Verfolgen geht nur für eine Bildebene über dem Grundmaterial, und nur,
   *  solange danach noch Clip übrig ist. */
  const kannVerfolgen = !nurTon && !!basisSrc && clip.duration - local > 0.4;

  const colour = clip.effects.find((e) => e.type === 'colorAdjust');
  const lut = clip.effects.find((e) => e.type === 'lut');
  const blur = clip.effects.find((e) => e.type === 'blur');
  const vignette = clip.effects.find((e) => e.type === 'vignette');
  const chroma = clip.effects.find((e) => e.type === 'chroma');
  const stabi = clip.effects.find((e) => e.type === 'stabi');
  const raederFx = clip.effects.find((e) => e.type === 'farbraeder');
  const raeder = raederFx?.type === 'farbraeder' ? raederFx : null;

  const setEffects = (next: Effect[]) =>
    dispatch({ op: 'setClipEffects', clipId: clip.id, effects: next } as Op);

  const upsert = (effect: Effect) =>
    setEffects([...clip.effects.filter((e) => e.type !== effect.type), effect]);

  const removeEffect = (type: Effect['type']) => setEffects(clip.effects.filter((e) => e.type !== type));

  /* --- Farbräder (S14) --------------------------------------------------
   *
   * Ein Rad hat zwei Achsen: warm/kühl und grün/magenta. Genau die stehen
   * hier als zwei Schieber -- ein rundes Rad ist mit dem Daumen auf einem
   * Telefon nicht zu treffen, und was man damit einstellt, sind ohnehin diese
   * beiden Zahlen. Die drei Zahlen dahinter (Rot, Grün, Blau) rechnet
   * `ausSchiebern`; zurück geht es mit `zuSchiebern`, damit die Schieber nach
   * der Pipette dort stehen, wo sie hingehören.
   */
  const RAD_BEREICHE = [
    { schluessel: 'tiefen' as const, label: 'Tiefen' },
    { schluessel: 'mitten' as const, label: 'Mitten' },
    { schluessel: 'hoehen' as const, label: 'Höhen' },
  ];
  const radWerte = (schluessel: 'tiefen' | 'mitten' | 'hoehen'): Dreiklang =>
    (raeder ? raeder[schluessel] : [0, 0, 0]) as Dreiklang;
  const setzeRaeder = (naechste: { tiefen: Dreiklang; mitten: Dreiklang; hoehen: Dreiklang }) => {
    const alle = [...naechste.tiefen, ...naechste.mitten, ...naechste.hoehen];
    // Alles auf null heisst „keine Farbräder“ und nicht „Farbräder, die
    // nichts tun“: sonst steht am Clip ein Effekt, der nur die Vorschau
    // bremst und im Blatt als „geändert“ gezählt wird.
    if (alle.every((v) => Math.abs(v) < 1e-6)) removeEffect('farbraeder');
    else upsert({ type: 'farbraeder', ...naechste });
  };
  const setzeRad = (schluessel: 'tiefen' | 'mitten' | 'hoehen', werte: Dreiklang) =>
    setzeRaeder({
      tiefen: schluessel === 'tiefen' ? werte : radWerte('tiefen'),
      mitten: schluessel === 'mitten' ? werte : radWerte('mitten'),
      hoehen: schluessel === 'hoehen' ? werte : radWerte('hoehen'),
    });

  /**
   * Die Pipette: ein Tipp auf etwas, das grau sein soll, und die Farben
   * stehen gerade. Gelesen wird aus dem Bild, das auch die Kacheln zeigen --
   * der Frame unter dem Abspielkopf. Kein zweiter Weg zum Bild, damit nicht
   * gemessen wird, was gar nicht dasteht.
   */
  const pipetteTippe = (ereignis: { clientX: number; clientY: number; currentTarget: HTMLImageElement }) => {
    const bild = ereignis.currentTarget;
    const kasten = bild.getBoundingClientRect();
    const breite = bild.naturalWidth;
    const hoehe = bild.naturalHeight;
    if (!breite || !hoehe || !kasten.width || !kasten.height) {
      toast('error', 'Das Bild ist noch nicht da.');
      return;
    }
    const x = Math.min(breite - 1, Math.max(0, Math.round(((ereignis.clientX - kasten.left) / kasten.width) * breite)));
    const y = Math.min(hoehe - 1, Math.max(0, Math.round(((ereignis.clientY - kasten.top) / kasten.height) * hoehe)));
    try {
      const leinwand = document.createElement('canvas');
      leinwand.width = breite;
      leinwand.height = hoehe;
      const stift = leinwand.getContext('2d');
      if (!stift) throw new Error('keine Leinwand');
      stift.drawImage(bild, 0, 0);
      const [r, g, b] = stift.getImageData(x, y, 1, 1).data;
      const werte = weissabgleich(r ?? 0, g ?? 0, b ?? 0);
      // Auf alle drei Bereiche: ein Farbstich vom Licht liegt über dem ganzen
      // Bild, nicht nur in den Mitten.
      setzeRaeder({ tiefen: werte, mitten: werte, hoehen: werte });
      setPipette(false);
      toast('success', `Gemessen: ${r}/${g}/${b} — als Grau gesetzt.`);
    } catch {
      // Ein Bild aus fremder Quelle macht die Leinwand unlesbar.
      toast('error', 'Aus diesem Bild lässt sich keine Farbe lesen.');
    }
  };

  // 7.25b: am grossen Schirm Reiter statt einer langen Spalte. Welche es
  // gibt, hängt am Clip: ein Tonclip hat kein Bild und keine Farbe.
  const reiterListe: { id: Reiter; label: string }[] = [
    ...(nurTon ? [] : [{ id: 'bild' as const, label: 'Bild' }]),
    { id: 'bewegung' as const, label: 'Bewegung' },
    ...(nurTon ? [] : [{ id: 'farbe' as const, label: 'Farbe' }]),
    ...(track ? [{ id: 'ton' as const, label: 'Ton' }] : []),
  ];
  const [pipette, setPipette] = useState(false);
  const [reiter, setReiter] = useState<Reiter>(() => (reiterListe.some((r) => r.id === letzterReiter) ? letzterReiter : reiterListe[0]!.id));
  const waehleReiter = (id: Reiter) => {
    letzterReiter = id;
    setReiter(id);
  };

  // Vorlagen (S24). Das Blatt tritt an die Stelle dieses hier und legt sich
  // nicht darüber: zwei Blätter übereinander sind zwei „Fertig“-Knöpfe, und
  // der obere liegt hinter dem Schleier des unteren.
  if (vorlagen) {
    return (
      <VorlagenSheet
        art="filter"
        aktuell={filterVorlageAus(clip)}
        anwenden={(daten) => {
          if (daten.art === 'filter') patch(clipPatch(daten));
        }}
        onClose={() => setVorlagen(false)}
      />
    );
  }

  return (
    <Sheet title={clip.label || 'Clip'} onClose={onClose}>
      <div className="inspektor" data-reiter-wahl={reiter}>
      <div className="reiter" role="tablist" aria-label="Bereiche">
        {reiterListe.map((r) => (
          <button key={r.id} role="tab" aria-selected={reiter === r.id} data-reiter={r.id} className={reiter === r.id ? 'an' : ''} onClick={() => waehleReiter(r.id)}>
            {r.label}
          </button>
        ))}
      </div>
      {/* Wo der Clip liegt und wie lang er ist: eine Auskunft, kein Regler.
          Beides zieht man in der Zeitleiste, und ein Schieber daneben, der
          dasselbe noch einmal kann, macht das Blatt nur länger. (Sascha, 6.
          September: „das können wir höchstens anzeigen“.) */}
      <div className="clip-stand">
        <div>
          <span>Ort</span>
          <strong data-ort>{zeit(clip.start, true)}</strong>
        </div>
        <div>
          <span>Länge</span>
          <strong data-laenge>{zeit(clip.duration, true)}</strong>
        </div>
        <div>
          <span>Spur</span>
          <strong>{track?.name || trackId}</strong>
        </div>
      </div>
      <p className="hint" style={{ marginTop: 0 }}>
        {nurTon ? 'Ort und Länge in der Zeitleiste, alles zum Klang unter „Ton“.' : 'Ort und Länge in der Zeitleiste, Position und Größe im Bild, Lautstärke unter „Ton“.'}
      </p>

      <Gruppe titel="Wiedergabe" reiter="bewegung" geaendert={geaendert.wiedergabe} onZuruecksetzen={() => patch(zuruecksetzen(clip, 'wiedergabe'))}>
      <Slider
        label={clip.speedBis === null ? 'Tempo' : 'Tempo am Anfang'}
        value={clip.speed}
        min={0.25}
        max={4}
        step={0.05}
        format={(v) => `${v.toFixed(2)}×`}
        onChange={(v) => patch({ speed: v })}
      />
      {/* Die Rampe (S6). Sascha: „Langsamer werden statt umschalten.“ Ein
          Schalter und erst dann ein zweiter Regler: solange niemand eine Rampe
          will, soll hier ein Regler stehen und nicht zwei. */}
      <label className="spread" style={{ marginBottom: 8 }} data-rampe={clip.speedBis === null ? 'aus' : 'an'}>
        <span className="sub">Langsamer oder schneller werden</span>
        <input
          type="checkbox"
          checked={clip.speedBis !== null}
          onChange={(e) => patch({ speedBis: e.target.checked ? clip.speed : null })}
          style={{ width: 22, height: 22, minHeight: 0 }}
        />
      </label>
      {clip.speedBis !== null ? (
        <Slider
          label="Tempo am Ende"
          value={clip.speedBis}
          min={0.25}
          max={4}
          step={0.05}
          format={(v) => `${v.toFixed(2)}×`}
          onChange={(v) => patch({ speedBis: v })}
        />
      ) : null}
      {/* atempo, nicht asetrate: die Tonhöhe bleibt, wo sie ist. Bei einer
          Stimme ist das der Unterschied zwischen „schneller“ und „Micky
          Maus“, und man sieht dem Regler nicht an, welches von beidem er
          tut. Auch in einer Rampe: dort wird der Ton in Stufen gerechnet, und
          jede Stufe hält die Tonhöhe. */}
      {nurTon ? <p className="hint" style={{ marginTop: -6 }}>Die Tonhöhe bleibt.</p> : null}
      </Gruppe>

      {/* Auch auf der Basisspur: seit ein Clip dort Anfasser hat, kann er sich
          auch bewegen -- Preview und Export setzen ihn dann wie eine Ebene auf
          den Hintergrund. */}
      {track?.kind === 'video' ? (
        <Gruppe
          titel="Bewegung (Keyframes)"
          reiter="bewegung"
          geaendert={geaendert.bewegung}
          bleibtZu
          onZuruecksetzen={() => patch(zuruecksetzen(clip, 'bewegung'))}
        >
          {/* Set a keyframe, move the layer with the handles, move on, set
              the next: the layer travels between them. Tapping a keyframe
              jumps there; the x removes it. */}
          <div className="chips">
            <button className="chip active" onClick={setKeyframeHere}>
              Keyframe bei {zeit(local, true)} setzen
            </button>
            {keyframes.map((k) => (
              <span key={k.at} className="chip" style={{ display: 'inline-flex', gap: 6, alignItems: 'center', padding: '4px 6px 4px 10px' }}>
                <button className="ghost" style={{ padding: 0, minHeight: 0, border: 'none' }} onClick={() => setPlayhead(clip.start + k.at)}>
                  {zeit(k.at, true)}
                </button>
                <button
                  className="ghost"
                  style={{ padding: '0 4px', minHeight: 0, border: 'none' }}
                  aria-label={`Keyframe bei ${zeit(k.at, true)} entfernen`}
                  onClick={() => setKeyframes(keyframes.filter((x) => x !== k))}
                >
                  ✕
                </button>
              </span>
            ))}
            {keyframes.length > 0 ? (
              <button className="chip" onClick={() => setKeyframes([])}>
                Alle entfernen
              </button>
            ) : null}
            {kannVerfolgen ? (
              <button className="chip" data-verfolgen disabled={verfolgt !== null} onClick={() => void verfolgen()}>
                {verfolgt === null ? 'Objekt verfolgen' : `Verfolgt… ${Math.round(verfolgt * 100)} %`}
              </button>
            ) : null}
          </div>
          {/* Wie die Strecke *ab* einem Keyframe verläuft (S30). Sie steht am
              Keyframe, an dem sie anfängt, und es gibt sie erst ab zweien --
              bei einem einzigen gibt es keine Strecke, über die zu entscheiden
              wäre. Voreingestellt ist „weich“, also das, was bis zum
              17. September das Einzige war. */}
          {keyframes.length > 1 ? (
            <>
              <h4 style={{ fontSize: 12, color: 'var(--muted)', margin: '12px 0 6px' }}>
                Verlauf ab {zeit(gewaehlterKeyframe.at, true)}
              </h4>
              <div className="chips" data-kurve={gewaehlterKeyframe.kurve}>
                {KURVEN.map((k) => (
                  <button
                    key={k}
                    className={`chip ${gewaehlterKeyframe.kurve === k ? 'active' : ''}`}
                    data-kurve-wahl={k}
                    onClick={() =>
                      setKeyframes(
                        keyframes.map((x) => (x.at === gewaehlterKeyframe.at ? { ...x, kurve: k } : x)),
                      )
                    }
                  >
                    {KURVEN_NAMEN[k]}
                  </button>
                ))}
              </div>
              <p className="hint">
                {gewaehlterKeyframe.kurve === 'linear'
                  ? 'Gleichbleibendes Tempo — für Abspänne und ruhige Fahrten.'
                  : gewaehlterKeyframe.kurve === 'rein'
                    ? 'Fängt langsam an und wird schneller.'
                    : gewaehlterKeyframe.kurve === 'raus'
                      ? 'Kommt langsam zur Ruhe.'
                      : 'Fährt an und kommt zur Ruhe — für fast alles das Richtige.'}
              </p>
            </>
          ) : null}
          {kannVerfolgen ? (
            <p className="hint" data-verfolgen-hinweis>
              Schieb die Ebene auf das, was sie begleiten soll, und tippe auf <em>Objekt verfolgen</em>. Ab
              hier entstehen {PUNKTE_JE_SEKUNDE} Punkte je Sekunde, bis der Clip endet oder die Spur abreißt.
            </p>
          ) : null}
          <p className="hint">
            {keyframes.length === 0
              ? 'Zwei Keyframes an zwei Stellen — dazwischen bewegt sich der Text von selbst.'
              : `${keyframes.length} Keyframe${keyframes.length === 1 ? '' : 's'}. Verschieben im Bild ändert den Keyframe am Abspielkopf oder setzt dort einen neuen.`}
          </p>
        </Gruppe>
      ) : null}

      {/* Die Form, in der die Ebene läuft (7.82). Nur für Ebenen -- die
          Basisspur *ist* das Bild, eine Form darin liesse schwarz darum
          herum stehen. */}
      {!isBaseTrack ? (
        <Gruppe
          titel="Form"
          reiter="bild"
          geaendert={
            clip.maske && clip.maske.form !== 'keine'
              ? [
                  { gruppe: 'bild' as const, label: 'Form', wert: MASKEN_NAMEN[clip.maske.form] },
                  // Die Überschrift sagt auch, dass die Form nicht mehr mittig
                  // sitzt -- sonst müsste man aufklappen, um es zu erfahren.
                  ...(maskeMittig(clip.maske)
                    ? []
                    : [{ gruppe: 'bild' as const, label: 'Lage', wert: 'verschoben' }]),
                  ...(clip.maske.gespiegelt ? [{ gruppe: 'bild' as const, label: 'Seite', wert: 'rechts' }] : []),
                ]
              : []
          }
          onZuruecksetzen={() => patch({ maske: null })}
        >
          <div className="chips" data-maskenwahl>
            {MASKENFORMEN.map((form) => (
              <button
                key={form}
                className={`chip${(clip.maske?.form ?? 'keine') === form ? ' active' : ''}`}
                data-maske={form}
                onClick={() =>
                  patch({
                    maske:
                      form === 'keine'
                        ? null
                        : {
                            form,
                            weich: clip.maske?.weich ?? 0,
                            // Die Lage bleibt stehen, wenn jemand die Form
                            // wechselt: wer die Blase am Kopf platziert hat und
                            // dann einen Kreis probiert, will den Kreis dort.
                            x: clip.maske?.x ?? 0.5,
                            y: clip.maske?.y ?? 0.5,
                            groesse: clip.maske?.groesse ?? 1,
                            drehung: clip.maske?.drehung ?? 0,
                            // Eine neue Gedankenblase kommt mit schwarzer
                            // Kontur, wie im Comic (7.224); eine Kontur, die
                            // schon da war, bleibt beim Wechsel der Form.
                            kontur: clip.maske ? clip.maske.kontur ?? null : form === 'blase' || form === 'sprechblase' ? { farbe: '#000000', staerke: KONTUR_VORGABE } : null,
                            gespiegelt: clip.maske?.gespiegelt,
                          },
                  })
                }
              >
                {MASKEN_NAMEN[form]}
              </button>
            ))}
          </div>
          {/* Ort, Grösse und Drehung der Form (7.82b). Nur wenn eine Form
              gewählt ist -- vier Regler unter „Ganzes Bild“ wären vier Regler
              für nichts. */}
          {clip.maske && clip.maske.form !== 'keine' ? (
            <div data-maskenlage style={{ marginTop: 12 }}>
              <Slider
                label="Nach rechts"
                value={clip.maske.x}
                min={-0.5}
                max={1.5}
                step={0.01}
                format={(v) => `${Math.round(v * 100)} %`}
                onChange={(v) => patch({ maske: { ...clip.maske!, x: v } })}
              />
              <Slider
                label="Nach unten"
                value={clip.maske.y}
                min={-0.5}
                max={1.5}
                step={0.01}
                format={(v) => `${Math.round(v * 100)} %`}
                onChange={(v) => patch({ maske: { ...clip.maske!, y: v } })}
              />
              <Slider
                label="Größe"
                value={clip.maske.groesse}
                min={0.1}
                max={3}
                step={0.01}
                format={(v) => `${v.toFixed(2)}×`}
                onChange={(v) => patch({ maske: { ...clip.maske!, groesse: v } })}
              />
              <Slider
                label="Drehung"
                value={clip.maske.drehung}
                min={-180}
                max={180}
                step={1}
                format={(v) => `${Math.round(v)}°`}
                onChange={(v) => patch({ maske: { ...clip.maske!, drehung: v } })}
              />
              {/* Wohin Bläschen oder Spitze zeigen (7.224, Teil 4) -- nur
                  bei den beiden Blasen; Kreis, Ellipse und Ecken sind
                  links wie rechts dieselben. */}
              {clip.maske.form === 'blase' || clip.maske.form === 'sprechblase' ? (
                <div className="chips" data-blasenseite style={{ marginTop: 12 }}>
                  {([false, true] as const).map((rechts) => (
                    <button
                      key={String(rechts)}
                      className={`chip${Boolean(clip.maske!.gespiegelt) === rechts ? ' active' : ''}`}
                      data-blasenseite-wahl={rechts ? 'rechts' : 'links'}
                      onClick={() => patch({ maske: { ...clip.maske!, gespiegelt: rechts } })}
                    >
                      {clip.maske!.form === 'blase' ? 'Bläschen' : 'Spitze'} {rechts ? 'rechts' : 'links'}
                    </button>
                  ))}
                </div>
              ) : null}
              {/* Die Kontur (7.224; Sascha: „Mehr wie ein Comic sollte sie
                  aussehen“): ein Strich innen am Rand der Form. */}
              <div className="chips" data-konturwahl style={{ marginTop: 12 }}>
                {KONTUR_FARBEN.map(([name, farbe]) => (
                  <button
                    key={name}
                    className={`chip${(clip.maske!.kontur?.farbe ?? null) === farbe ? ' active' : ''}`}
                    data-kontur-farbe={farbe ?? 'keine'}
                    onClick={() =>
                      patch({
                        maske: {
                          ...clip.maske!,
                          kontur: farbe ? { farbe, staerke: clip.maske!.kontur?.staerke ?? KONTUR_VORGABE } : null,
                        },
                      })
                    }
                  >
                    {name}
                  </button>
                ))}
              </div>
              {clip.maske.kontur ? (
                <Slider
                  label="Kontur"
                  value={clip.maske.kontur.staerke}
                  min={0.004}
                  max={0.04}
                  step={0.002}
                  format={(v) => `${(v * 100).toFixed(1)} %`}
                  onChange={(v) => patch({ maske: { ...clip.maske!, kontur: { ...clip.maske!.kontur!, staerke: v } } })}
                />
              ) : null}
            </div>
          ) : null}
        </Gruppe>
      ) : null}

      {isBaseTrack ? (
        <Gruppe titel="Übergang zum vorigen Clip" reiter="bild" geaendert={geaendert.uebergang} onZuruecksetzen={() => dispatch({ op: 'setTransition', clipId: clip.id, transition: null } as Op)}>
          <div className="chips">
            <button
              className={`chip ${clip.transitionIn ? '' : 'active'}`}
              onClick={() => dispatch({ op: 'setTransition', clipId: clip.id, transition: null } as Op)}
            >
              Harter Schnitt
            </button>
            {TRANSITIONS.map((kind) => (
              <button
                key={kind}
                className={`chip ${clip.transitionIn?.kind === kind ? 'active' : ''}`}
                onClick={() =>
                  dispatch({
                    op: 'setTransition',
                    clipId: clip.id,
                    transition: { kind, duration: clip.transitionIn?.duration ?? 0.5 },
                  } as Op)
                }
              >
                {TRANSITION_LABELS[kind]}
              </button>
            ))}
          </div>
          {clip.transitionIn ? (
            <div style={{ marginTop: 12 }}>
              <Slider
                label="Dauer"
                value={clip.transitionIn.duration}
                min={0.1}
                max={Math.min(3, clip.duration)}
                step={0.05}
                format={(v) => `${v.toFixed(2)}s`}
                onChange={(v) =>
                  dispatch({
                    op: 'setTransition',
                    clipId: clip.id,
                    transition: { kind: clip.transitionIn!.kind, duration: v },
                  } as Op)
                }
              />
              <p className="hint">
                Überlappt mit dem vorherigen Clip. Reicht dessen Material nicht, wird die Blende gekürzt.
              </p>
            </div>
          ) : null}
        </Gruppe>
      ) : null}

      {/* Was ein Bild braucht: Look, Farbe, Deckkraft. Fuer einen Tonclip
          steht davon nichts da -- er hat kein Bild, an dem es wirken koennte
          (7.22). */}
      {/* Vorlagen (S24): einmal einstellen, immer wieder benutzen. Der Knopf
          steht im Reiter „farbe“, weil dort das meiste davon eingestellt wird
          -- und die Vorlage nimmt auch die Werkzeuge für den Ton mit, damit
          eine Stimme nicht zweimal von Hand gleich klingen muss. */}
      <div className="row" data-reiter="farbe" style={{ marginBottom: 10 }}>
        <div className="grow">
          <div className="title">Vorlagen</div>
          <div className="sub">Was hier eingestellt ist, sichern — oder Gesichertes anwenden.</div>
        </div>
        <button className="small" data-vorlagen-oeffnen onClick={() => setVorlagen(true)}>
          Öffnen
        </button>
      </div>

      {nurTon ? null : (
        <>
        <Gruppe titel="Look" reiter="farbe" geaendert={geaendert.look} onZuruecksetzen={() => patch(zuruecksetzen(clip, 'look'))}>
        {/* Kacheln mit dem eigenen Bild statt einer Namensreihe (7.16): „Kino“
            und „Kräftig“ sind Wörter, unter denen sich jeder etwas anderes
            vorstellt. Der Frame kommt vom Abspielkopf, sonst vom Standbild. */}
        <div className="kachel-reihe" data-looks>
          <EffektKachel
            bild={kachelbild}
            effects={ohneLook}
            label="Ohne"
            aktiv={!lut}
            kennung="look-ohne"
            onClick={() => removeEffect('lut')}
          />
          {LOOKS.map((look) => (
            <EffektKachel
              key={look.name}
              bild={kachelbild}
              effects={[...ohneLook, { type: 'lut', name: look.name, intensity: lut?.type === 'lut' ? lut.intensity : 0.8 }]}
              label={look.label}
              aktiv={lut?.type === 'lut' && lut.name === look.name}
              kennung={`look-${look.name}`}
              onClick={() => upsert({ type: 'lut', name: look.name, intensity: lut?.type === 'lut' ? lut.intensity : 0.8 })}
            />
          ))}
        </div>
        {lut?.type === 'lut' ? (
          <div style={{ marginTop: 12 }}>
            <Slider
              label="Stärke"
              value={lut.intensity}
              min={0}
              max={1}
              format={(v) => `${Math.round(v * 100)}%`}
              onChange={(v) => upsert({ type: 'lut', name: lut.name, intensity: v })}
            />
            <p className="hint">Die Vorschau ist eine Näherung, der Export rechnet genau.</p>
          </div>
        ) : null}
        </Gruppe>

        {/* Farbräder und Weissabgleich (S14). Sascha: „Drei Bereiche (Tiefen,
            Mitten, Höhen) statt eines Reglers, und ein Klick auf etwas Graues
            richtet die Farben aus.“ Steht beim Look und nicht bei den
            Bildreglern: Look und Farbräder sind beide Farbe, Helligkeit und
            Schärfe sind es nicht. */}
        <Gruppe
          titel="Farbräder"
          reiter="farbe"
          geaendert={geaendert.farbraeder}
          onZuruecksetzen={() => patch(zuruecksetzen(clip, 'farbraeder'))}
        >
        <div className="field" data-weissabgleich>
          <span>Weissabgleich</span>
          <div className="chips">
            <button
              className={`chip ${pipette ? 'active' : ''}`}
              data-pipette
              aria-pressed={pipette}
              onClick={() => setPipette(!pipette)}
            >
              {pipette ? 'Abbrechen' : 'Pipette'}
            </button>
          </div>
        </div>
        {pipette ? (
          kachelbild ? (
            <div className="pipette-bild" data-pipette-bild>
              <img src={kachelbild} alt="" onClick={pipetteTippe} data-pipette-ziel />
              <p className="hint">Tippen Sie auf etwas, das grau sein soll — eine Wand, ein Hemd, eine Graukarte.</p>
            </div>
          ) : (
            <p className="hint" data-pipette-ohne>
              Von diesem Clip liegt gerade kein Bild vor, aus dem sich eine Farbe lesen liesse.
            </p>
          )
        ) : null}
        {RAD_BEREICHE.map(({ schluessel, label }) => {
          const werte = radWerte(schluessel);
          const { waerme, gruenMagenta } = zuSchiebern(werte);
          return (
            <div key={schluessel} data-farbrad={schluessel}>
              <Slider
                label={`${label}: kühl ↔ warm`}
                value={waerme}
                min={-1}
                max={1}
                step={0.05}
                format={radText}
                onChange={(v) => setzeRad(schluessel, ausSchiebern(v, gruenMagenta))}
              />
              <Slider
                label={`${label}: grün ↔ magenta`}
                value={gruenMagenta}
                min={-1}
                max={1}
                step={0.05}
                format={radText}
                onChange={(v) => setzeRad(schluessel, ausSchiebern(waerme, v))}
              />
            </div>
          );
        })}
        <p className="hint">
          Die Vorschau ist eine Näherung, der Export rechnet genau.
        </p>
        </Gruppe>

        <Gruppe titel="Bild" reiter="bild" geaendert={geaendert.bild} onZuruecksetzen={() => patch(zuruecksetzen(clip, 'bild'))}>
        {/* Zuerst das, was man fast immer will (7.15). „Wärmer“ und „kühler“
            stehen hier bewusst nicht: eine Farbtemperatur lässt sich mit
            Helligkeit, Kontrast, Sättigung und Gamma nicht drehen, und ein
            Knopf, der etwas anderes tut als sein Name sagt, ist schlechter
            als keiner. Dafür gibt es die Looks eine Zeile darüber. */}
        <div className="kachel-reihe" data-bildvorlagen>
          <EffektKachel
            bild={kachelbild}
            effects={ohneFarbe}
            label="Wie aufgenommen"
            aktiv={!colour}
            kennung="bild-ohne"
            onClick={() => removeEffect('colorAdjust')}
          />
          {BILD_VORLAGEN.map((v) => (
            <EffektKachel
              key={v.name}
              bild={kachelbild}
              effects={[...ohneFarbe, { type: 'colorAdjust', ...v.wirkung }]}
              label={v.label}
              aktiv={vorlage === v.name}
              kennung={`vorlage-${v.name}`}
              onClick={() => upsert({ type: 'colorAdjust', ...v.wirkung })}
            />
          ))}
        </div>
        <p className="hint" style={{ marginTop: 8 }}>
          {vorlage ? 'Darunter lässt sich alles einzeln nachstellen.' : colour ? 'Von Hand gestellt.' : 'Oder einzeln, darunter.'}
        </p>
        <Slider
          label="Helligkeit"
          value={colour?.type === 'colorAdjust' ? colour.brightness : 0}
          min={-0.5}
          max={0.5}
          onChange={(v) =>
            upsert({
              type: 'colorAdjust',
              brightness: v,
              contrast: colour?.type === 'colorAdjust' ? colour.contrast : 1,
              saturation: colour?.type === 'colorAdjust' ? colour.saturation : 1,
              gamma: colour?.type === 'colorAdjust' ? colour.gamma : 1,
            })
          }
        />
        <Slider
          label="Kontrast"
          value={colour?.type === 'colorAdjust' ? colour.contrast : 1}
          min={0.5}
          max={2}
          onChange={(v) =>
            upsert({
              type: 'colorAdjust',
              brightness: colour?.type === 'colorAdjust' ? colour.brightness : 0,
              contrast: v,
              saturation: colour?.type === 'colorAdjust' ? colour.saturation : 1,
              gamma: colour?.type === 'colorAdjust' ? colour.gamma : 1,
            })
          }
        />
        <Slider
          label="Sättigung"
          value={colour?.type === 'colorAdjust' ? colour.saturation : 1}
          min={0}
          max={2}
          onChange={(v) =>
            upsert({
              type: 'colorAdjust',
              brightness: colour?.type === 'colorAdjust' ? colour.brightness : 0,
              contrast: colour?.type === 'colorAdjust' ? colour.contrast : 1,
              saturation: v,
              gamma: colour?.type === 'colorAdjust' ? colour.gamma : 1,
            })
          }
        />
        <Slider
          label="Weichzeichnen"
          value={blur?.type === 'blur' ? blur.amount : 0}
          min={0}
          max={20}
          step={0.5}
          format={(v) => v.toFixed(0)}
          onChange={(v) => (v === 0 ? removeEffect('blur') : upsert({ type: 'blur', amount: v }))}
        />
        <Slider
          label="Vignette"
          value={vignette?.type === 'vignette' ? vignette.amount : 0}
          min={0}
          max={1}
          step={0.05}
          format={(v) => `${Math.round(v * 100)}%`}
          onChange={(v) => (v === 0 ? removeEffect('vignette') : upsert({ type: 'vignette', amount: v }))}
        />

        {/* Bildstabilisierung (S12). Sascha: „Verwackelte Handaufnahmen ruhig
            rechnen.“ Eine Zahl, kein Dutzend -- dahinter stecken zwei
            Durchgänge von ffmpeg, und beide bekommen ihre Werte aus ihr. */}
        <Slider
          label="Verwacklung ausgleichen"
          value={stabi?.type === 'stabi' ? stabi.staerke : 0}
          min={0}
          max={1}
          step={0.1}
          format={(v) => (v === 0 ? 'aus' : `${Math.round(v * 100)}%`)}
          onChange={(v) => (v === 0 ? removeEffect('stabi') : upsert({ type: 'stabi', staerke: Math.max(0.1, v) }))}
        />
        {stabi?.type === 'stabi' ? (
          <p className="hint" data-stabi-hinweis>
            Wird beim Export gerechnet — zweimal durch das Material, einmal messen und einmal
            ausgleichen. Die Vorschau zeigt den Clip so, wie er aufgenommen wurde.
          </p>
        ) : null}

        {/* Greenscreen (S11). Sascha: „Eine Farbe herausschneiden und etwas
            dahinter setzen.“ Steht hier bei den Bildsachen und nicht in einem
            eigenen Blatt: es ist eine Eigenschaft *dieses* Clips, so wie das
            Weichzeichnen. */}
        <div className="field" data-greenscreen>
          <span>Greenscreen</span>
          <div className="chips">
            <button
              className={`chip ${chroma ? '' : 'active'}`}
              data-chroma="aus"
              onClick={() => removeEffect('chroma')}
            >
              Aus
            </button>
            {([['#00b140', 'Grün'], ['#0047bb', 'Blau']] as const).map(([farbe, name]) => (
              <button
                key={farbe}
                className={`chip ${chroma?.type === 'chroma' && chroma.color === farbe ? 'active' : ''}`}
                data-chroma={farbe}
                onClick={() =>
                  upsert({
                    type: 'chroma',
                    color: farbe,
                    toleranz: chroma?.type === 'chroma' ? chroma.toleranz : 0.2,
                    weich: chroma?.type === 'chroma' ? chroma.weich : 0.05,
                  })
                }
              >
                {name}
              </button>
            ))}
          </div>
        </div>
        {chroma?.type === 'chroma' ? (
          <>
            <Slider
              label="Wie viel Farbe weg"
              value={chroma.toleranz}
              min={0.05}
              max={0.6}
              step={0.01}
              format={(v) => `${Math.round(v * 100)}%`}
              onChange={(v) => upsert({ ...chroma, toleranz: v })}
            />
            <Slider
              label="Weiche Kante"
              value={chroma.weich}
              min={0}
              max={0.3}
              step={0.01}
              format={(v) => `${Math.round(v * 100)}%`}
              onChange={(v) => upsert({ ...chroma, weich: v })}
            />
            <p className="hint">
              Sichtbar wird es im Export und auf einer Ebene über der untersten Bildspur — unter der
              untersten liegt nichts, was durchscheinen könnte. Die Vorschau zeigt den Clip
              unverändert.
            </p>
          </>
        ) : null}

        {/* Waagerecht, Senkrecht und Größe standen hier als drei Schieber. Sie
            sind weg: die Ebene wird im Bild angefasst und gezogen, und zwar in
            einem Zug (7.6/7.6a). Die Deckkraft bleibt -- die hat keinen
            Anfasser, weil man sie nicht greifen kann. */}
        </Gruppe>

        <Gruppe titel="Ebene" reiter="bild" geaendert={geaendert.ebene} onZuruecksetzen={() => patch(zuruecksetzen(clip, 'ebene'))}>
        <Slider
          label="Deckkraft"
          value={clip.transform.opacity}
          min={0}
          max={1}
          format={(v) => `${Math.round(v * 100)}%`}
          onChange={(v) => patch({ transform: { ...clip.transform, opacity: v } })}
        />
        </Gruppe>
        </>
      )}

      {/* Der Pegel der ganzen Spur und das Absenken unter Sprache gehören der
          Spur und nicht diesem Clip. Sie stehen deshalb in ihrem eigenen
          Blatt; hier ist nur die Tür dorthin (7.32a). */}
      {/* „Spur“ bleibt bewusst *keine* aufklappbare Gruppe: dahinter steht
          keine Einstellung, sondern eine Tür. Eine Tür hinter einer
          zugeklappten Überschrift ist eine Tür, die niemand findet. */}
      {/* **Die Lautstärke dieses Clips** (Sascha, 4. Oktober: „Warum hat ein
          overlay keine Lautstärke Einstellungen“). Oben im Blatt stand „Lautstärke
          unter ‚Ton‘“ -- und unter „Ton“ stand nur die Tür zur Spur. Der Regler
          gab es nur hinter „Mehr → Pegel“. Jetzt hier, für jeden Clip mit Ton,
          auf welcher Spur auch immer; der Verlauf mit Punkten bleibt im Blatt
          „Ton“. */}
      {mitTon ? (
        <Gruppe titel="Lautstärke" reiter="ton" geaendert={geaendert.ton} onZuruecksetzen={() => patch(zuruecksetzen(clip, 'ton'))}>
          <div data-clip-lautstaerke>
            <div className="field">
              <div className="spread">
                <span>{pegelVerlauf ? 'Pegel (mit Verlauf)' : 'Pegel'}</span>
                <strong data-clip-gain>{clip.muted ? 'stumm' : `${Math.round(clip.gain * 100)} %`}</strong>
              </div>
              <input
                type="range"
                min={0}
                max={200}
                step={1}
                value={Math.round(clip.gain * 100)}
                disabled={clip.muted || pegelVerlauf}
                onChange={(e) => patch({ gain: Number(e.target.value) / 100 })}
                aria-label="Lautstärke"
                data-clip-gain-regler
              />
            </div>
            <div className="chips">
              <button className={`chip ${clip.muted ? 'active' : ''}`} data-clip-stumm onClick={() => patch({ muted: !clip.muted })}>
                {clip.muted ? 'Ton an' : 'Stumm'}
              </button>
              <button className="chip" data-clip-pegelverlauf onClick={onOpenTon}>
                Pegel über die Zeit …
              </button>
            </div>
            {pegelVerlauf ? <p className="hint">Dieser Clip hat Pegelpunkte; sie stellt man im Verlauf.</p> : null}
          </div>
        </Gruppe>
      ) : null}
      {track ? (
        <section className="einstell-gruppe offen" data-gruppe="Spur" data-tuer data-reiter="ton">
          <div className="gruppe-kopf">
            <span className="gruppe-titel" style={{ cursor: 'default' }}>
              <span className="titel">Spur</span>
            </span>
          </div>
          <button className="row" data-spur-blatt onClick={() => onOpenSpur(track.id)}>
            Spur „{track.name || track.id}“ einstellen
          </button>
          <p className="hint" style={{ marginTop: 0 }}>Pegel, Absenken, Sperre.</p>
        </section>
      ) : null}
      </div>
    </Sheet>
  );
}

type Reiter = 'bild' | 'bewegung' | 'farbe' | 'ton';
/** Der zuletzt gewählte Reiter bleibt, wenn das Blatt zu- und wieder aufgeht. */
let letzterReiter: Reiter = 'bild';
